import {
  getDocument,
  GlobalWorkerOptions,
  ImageKind,
  OPS,
  version,
  type PDFPageProxy,
} from "pdfjs-dist";

type PdfRaster = {
  data?: Uint8ClampedArray | Uint8Array;
  width?: number;
  height?: number;
  kind?: number;
};

let workerConfigured = false;

function ensurePdfWorker() {
  if (workerConfigured) return;
  GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.js`;
  workerConfigured = true;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function canvasToPng(canvas: HTMLCanvasElement) {
  return canvas.toDataURL("image/png");
}

function rasterToDataUrl(img: PdfRaster): string | null {
  if (!img.data || !img.width || !img.height) return null;
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const imageData = ctx.createImageData(img.width, img.height);
  const src = img.data;
  const dest = imageData.data;
  const pixelCount = img.width * img.height;

  if (img.kind === ImageKind.RGBA_32BPP && src.length >= dest.length) {
    dest.set(src.subarray(0, dest.length));
  } else if (img.kind === ImageKind.RGB_24BPP || src.length === pixelCount * 3) {
    let si = 0;
    for (let di = 0; di < dest.length; di += 4) {
      dest[di] = src[si++];
      dest[di + 1] = src[si++];
      dest[di + 2] = src[si++];
      dest[di + 3] = 255;
    }
  } else if (src.length === pixelCount) {
    for (let i = 0; i < pixelCount; i++) {
      const gray = src[i];
      const di = i * 4;
      dest[di] = gray;
      dest[di + 1] = gray;
      dest[di + 2] = gray;
      dest[di + 3] = 255;
    }
  } else {
    return null;
  }

  ctx.putImageData(imageData, 0, 0);
  return canvasToPng(canvas);
}

function drawableToDataUrl(img: CanvasImageSource, width: number, height: number) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);
  return canvasToPng(canvas);
}

function pdfImageToDataUrl(img: unknown): string | null {
  if (!img || typeof img !== "object") return null;
  if (typeof ImageBitmap !== "undefined" && img instanceof ImageBitmap) {
    return drawableToDataUrl(img, img.width, img.height);
  }
  if (typeof HTMLImageElement !== "undefined" && img instanceof HTMLImageElement) {
    return drawableToDataUrl(img, img.naturalWidth || img.width, img.naturalHeight || img.height);
  }
  return rasterToDataUrl(img as PdfRaster);
}

function getNamedImage(page: PDFPageProxy, name: string) {
  return new Promise<unknown>((resolve) => {
    try {
      if (page.objs.has(name)) {
        resolve(page.objs.get(name));
        return;
      }
      page.objs.get(name, (value: unknown) => resolve(value ?? null));
    } catch {
      resolve(null);
    }
  });
}

async function extractPageImages(page: PDFPageProxy) {
  const ops = await page.getOperatorList();
  const urls: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < ops.fnArray.length; i++) {
    const fn = ops.fnArray[i];
    if (
      fn !== OPS.paintImageXObject &&
      fn !== OPS.paintImageXObjectRepeat &&
      fn !== OPS.paintXObject
    ) {
      continue;
    }
    const name = ops.argsArray[i]?.[0];
    if (typeof name !== "string" || seen.has(name)) continue;
    seen.add(name);
    const dataUrl = pdfImageToDataUrl(await getNamedImage(page, name));
    if (dataUrl) urls.push(dataUrl);
  }

  return urls;
}

async function extractPageTextHtml(page: PDFPageProxy) {
  const content = await page.getTextContent();
  const lines = new Map<number, string[]>();

  for (const item of content.items) {
    if (!("str" in item) || !item.str.trim()) continue;
    const y = Math.round((item.transform?.[5] ?? 0) / 6) * 6;
    const words = lines.get(y) ?? [];
    words.push(item.str);
    lines.set(y, words);
  }

  return [...lines.entries()]
    .sort((left, right) => right[0] - left[0])
    .map(([, words]) => `<p>${escapeHtml(words.join(" ").replace(/\s+/g, " "))}</p>`)
    .join("");
}

async function extractPageText(page: PDFPageProxy): Promise<string> {
  const content = await page.getTextContent();
  const lines = new Map<number, string[]>();

  for (const item of content.items) {
    if (!("str" in item) || !item.str.trim()) continue;
    const y = Math.round((item.transform?.[5] ?? 0) / 6) * 6;
    const words = lines.get(y) ?? [];
    words.push(item.str);
    lines.set(y, words);
  }

  return [...lines.entries()]
    .sort((left, right) => right[0] - left[0])
    .map(([, words]) => words.join(" ").replace(/\s+/g, " ").trim())
    .filter((line) => line.length > 0)
    .join("\n");
}

export async function extractPdfText(file: File): Promise<string> {
  ensurePdfWorker();
  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await getDocument({ data }).promise;
  const pageTexts: string[] = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const pageText = await extractPageText(page);
    if (pageText) {
      pageTexts.push(pageText);
    }
  }

  const text = pageTexts.join("\n\n").trim();
  if (!text) {
    throw new Error("No readable text found in this PDF.");
  }
  return text;
}

export async function extractPdfHtml(file: File): Promise<string> {
  ensurePdfWorker();
  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await getDocument({ data }).promise;
  const parts: string[] = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const textHtml = await extractPageTextHtml(page);
    if (textHtml) parts.push(textHtml);
    const images = await extractPageImages(page);
    parts.push(
      ...images.map((url) => `<figure><img src="${url}" alt="" /></figure>`)
    );
  }

  const html = parts.join("");
  if (!html.trim()) {
    throw new Error("No readable content found in this PDF.");
  }
  return html;
}
